import { Fragment, useEffect, useRef, useState } from "react";
import type { ReactNode, SVGProps } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { PawIcon, ShieldIcon, VenusIcon, CalendarIcon, HomeIcon, Button, Modal } from "@fawkes/ui";

import { LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Page } from "../../shell/nav";
import type { Locale } from "../../i18n/messages";
import { AuthImage } from "./AuthImage";
import { formatDateLong, formatDateShort, formatWeight } from "./format";
import { PortalWizardRunner } from "./PortalWizardRunner";
import type {
  PortalAnimals,
  PortalAnimalDetail as PortalAnimalDetailData,
  PortalRichNote,
  PortalVaccination,
  PortalWeight,
  PortalWizardOption,
  PortalWizardRun,
} from "./types";
import "./animal-detail.css";

// Layout (2026-09 ontwerp): a top bar (back + vorig/volgend dier), a hero card (photo, name, species/breed/sex,
// geboortedatum / in opvang sinds / verblijfduur, Acties-menu) and tabs. Overzicht shows a 3-column summary
// (Basisinformatie | Gewicht + Voeding | Medische aandachtspunten + Recente notities); the full tiles live on
// the Verzorging / Medisch / Notities tabs, Logboek merges everything the foster may see into one timeline.
// Every tile, field, tab and action follows the organization's foster policy (hidden/readonly/editable/deletable).
//
// jmix-8.5: the foster animal-detail page — mirrors the SAME tile set as the backoffice animal view
// (Basisinformatie, Bescherming, Notities, Taken & planning, Registraties & Wizards, Media & Documenten),
// in the foster (green/warm) palette, read-only. Only the FEDERATED data is filled today:
// Basisinformatie (the subset of fields the foster API carries) + Notities. The other tiles — and the
// non-federated Basis fields — render a "nog niet beschikbaar via het portaal" state until each is
// federated; per-tile visibility/permission (zien/niet zien/lezen/wijzigen) is backoffice-controlled (planned).

function ChevronLeftIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <polyline points="15 6 9 12 15 18" />
    </svg>
  );
}
function ChevronRightIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
function SyringeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M18 2l4 4M17 3l4 4-1.5 1.5-4-4zM14.5 5.5l4 4M3 21l7-7M8 16l-1.5 1.5M11 13l1.5 1.5M14.5 5.5l-9 9L3 21" />
    </svg>
  );
}
function NoteIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M21 11.5a8.4 8.4 0 0 1-11.9 7.6L3 21l1.9-6.1A8.4 8.4 0 1 1 21 11.5z" />
    </svg>
  );
}
function WizardIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M5 21l10-10M14 5l1.5 1.5M18 3l.7 2.3L21 6l-2.3.7L18 9l-.7-2.3L15 6l2.3-.7L18 3zM6 13l.6 1.8L8.5 15.5 6.7 16.1 6 18l-.6-1.9L3.5 15.5l1.9-.7L6 13z" />
    </svg>
  );
}

function ScaleIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M12 4v16M7 20h10M4 9l4-5 4 5a4 4 0 0 1-8 0zM12 9l4-5 4 5a4 4 0 0 1-8 0z" />
    </svg>
  );
}
function BowlIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M6 3v9M10 3v9M6 8h4M8 12v9M17 3c2 2 3 5 3 8 0 2-1 3-3 3v7M15 3v18" />
    </svg>
  );
}
function ClipboardIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4a3 3 0 0 1 6 0M9 9h6M9 13h6M9 17h4" />
    </svg>
  );
}
/** Male (Mars) glyph — the mockup's sex tag icon for a male animal. */
function MarsIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx="10" cy="14" r="5" />
      <path d="M14 10l6-6M15 4h5v5" />
    </svg>
  );
}

/** "8 j 11 mnd" — the age badge next to the name, like the backoffice. null when no/invalid DOB. */
function ageLabel(dobIso: string | null, t: ReturnType<typeof useTranslate>): string | null {
  if (!dobIso) {
    return null;
  }
  const dob = new Date(`${dobIso}T00:00:00`);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const now = new Date();
  let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
  if (now.getDate() < dob.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    return null;
  }
  return `${Math.floor(months / 12)} ${t("portal.animalDetail.age.years")} ${months % 12} ${t("portal.animalDetail.age.months")}`;
}

/** The mockup's sex-tag glyph: Mars for a male animal, Venus otherwise (matches the hero tags). */
function SexIcon({ sex, ...p }: { sex: string | null } & SVGProps<SVGSVGElement>) {
  const male = (sex ?? "").toLowerCase().startsWith("m");
  return male ? <MarsIcon {...p} /> : <VenusIcon {...p} />;
}

function sourceLabel(source: string | null, t: ReturnType<typeof useTranslate>): string {
  return source === "person"
    ? t("portal.animalDetail.bySource.person")
    : t("portal.animalDetail.bySource.organization");
}

function Tile({ icon, title, children, action, footer }: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="fad-tile">
      <div className="fad-tile-head">
        <h2>
          <span className="fad-tile-icon">{icon}</span>
          {title}
        </h2>
        {action ? <span className="fad-tile-action">{action}</span> : null}
      </div>
      <div className="fad-tile-body">{children}</div>
      {footer ? <div className="fad-tile-foot">{footer}</div> : null}
    </section>
  );
}

function PlayIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  );
}

function PencilIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m4 5v6m6-6v6" />
    </svg>
  );
}

function PlusIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}


/** Overzicht-tab. */
function GridIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

/** Verzorging-tab. */
function HeartIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

/** Logboek-tab. */
function BookIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
      <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/** Medische aandachtspunten. */
function StethoscopeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M5 3v6a5 5 0 0 0 10 0V3" />
      <path d="M10 14v2a5 5 0 0 0 10 0v-2" />
      <circle cx="20" cy="11" r="2" />
    </svg>
  );
}

/** Ontworming / ontvlooiing in de medische lijst. */
function PillIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M10.5 20.5a4.95 4.95 0 0 1-7-7l10-10a4.95 4.95 0 0 1 7 7z" />
      <path d="M8.5 8.5l7 7" />
    </svg>
  );
}

/** Acties-menu. */
function ChevronDownIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** One Logboek line — built from the federated notes / weights / feeding log / vaccinations. */
type LogEntry = {
  key: string;
  kind: "note" | "weight" | "feeding" | "vaccination";
  date: string;
  title: string;
  detail: string | null;
};

type DetailTab = "overview" | "care" | "medical" | "notes" | "log";

/** Gewichtsgrafiek (backoffice-tegel): SVG-lijn met vlakvulling, rasterlijnen en een periodefilter
 *  (Laatste 3 maanden / Laatste maand / Alles — default 3 maanden, zoals de backoffice-grafiek). */
function WeightChart({
  weights,
  labels,
}: {
  weights: PortalWeight[];
  labels: { m3: string; m1: string; all: string; few: string };
}) {
  const [period, setPeriod] = useState<"3m" | "1m" | "all">("3m");
  const all = weights
    .filter((w) => w.measuredAt != null)
    .map((w) => ({ ts: Date.parse(w.measuredAt as string), kg: w.kg }))
    .filter((p) => !Number.isNaN(p.ts))
    .sort((a, b) => a.ts - b.ts);
  if (all.length < 2) {
    return null;
  }
  const cutoff =
    period === "all" ? Number.NEGATIVE_INFINITY : Date.now() - (period === "3m" ? 92 : 31) * 86_400_000;
  const windowed = all.filter((p) => p.ts >= cutoff);
  // Te weinig metingen in het gekozen venster → val terug op alle metingen (met melding).
  const points = windowed.length >= 2 ? windowed : all;

  const W = 560;
  const H = 190;
  const PAD = { l: 46, r: 14, t: 12, b: 26 };
  const kgs = points.map((p) => p.kg);
  const rawMin = Math.min(...kgs);
  const rawMax = Math.max(...kgs);
  const padKg = Math.max((rawMax - rawMin) * 0.15, 0.4);
  const minKg = Math.max(0, rawMin - padKg);
  const maxKg = rawMax + padKg;
  const minTs = points[0].ts;
  const maxTs = points[points.length - 1].ts;
  const x = (ts: number) => PAD.l + ((ts - minTs) / (maxTs - minTs || 1)) * (W - PAD.l - PAD.r);
  const y = (kg: number) => H - PAD.b - ((kg - minKg) / (maxKg - minKg || 1)) * (H - PAD.t - PAD.b);
  const fmtKg = (kg: number) => (Math.round(kg * 10) / 10).toLocaleString("nl-NL");
  const fmtDate = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };
  const gridLines = [0, 1, 2, 3].map((i) => minKg + ((maxKg - minKg) * i) / 3);
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(p.ts).toFixed(1)},${y(p.kg).toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L${x(maxTs).toFixed(1)},${(H - PAD.b).toFixed(1)} L${x(minTs).toFixed(1)},${(
    H - PAD.b
  ).toFixed(1)} Z`;
  const midTs = minTs + (maxTs - minTs) / 2;

  return (
    <div className="fad-weight-chartwrap">
      <div className="fad-weight-chartbar">
        <select
          className="fad-input fad-weight-period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as typeof period)}
          aria-label="Periode"
        >
          <option value="3m">{labels.m3}</option>
          <option value="1m">{labels.m1}</option>
          <option value="all">{labels.all}</option>
        </select>
        {windowed.length < 2 && period !== "all" ? <span className="fad-weight-few">{labels.few}</span> : null}
      </div>
      <svg className="fad-weight-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Gewicht (kg)">
        {gridLines.map((kg, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={y(kg)} x2={W - PAD.r} y2={y(kg)} stroke="#eceee8" strokeWidth="1" />
            <text x={PAD.l - 8} y={y(kg) + 3.5} textAnchor="end" fontSize="10" fill="#6b7280">
              {fmtKg(kg)}
            </text>
          </g>
        ))}
        <path d={areaPath} fill="rgba(46, 125, 79, 0.09)" stroke="none" />
        <path d={linePath} fill="none" stroke="#2e7d4f" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={x(p.ts)} cy={y(p.kg)} r="3.5" fill="#2e7d4f" stroke="#fff" strokeWidth="1.5">
            <title>{`${fmtDate(p.ts)}: ${fmtKg(p.kg)} kg`}</title>
          </circle>
        ))}
        <text x={PAD.l} y={H - 8} textAnchor="start" fontSize="10" fill="#6b7280">
          {fmtDate(minTs)}
        </text>
        {maxTs - minTs > 21 * 86_400_000 ? (
          <text x={x(midTs)} y={H - 8} textAnchor="middle" fontSize="10" fill="#6b7280">
            {fmtDate(midTs)}
          </text>
        ) : null}
        <text x={W - PAD.r} y={H - 8} textAnchor="end" fontSize="10" fill="#6b7280">
          {fmtDate(maxTs)}
        </text>
      </svg>
    </div>
  );
}

/** Vandaag als ISO-datum (voor de gewicht-dialoog). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Gedrag-keuzes bij een voedingslogregel — dezelfde enum-codes als de backoffice-voedingsdialoog
 *  (AnimalFeedingCommandService.BEHAVIOR_CODES). */
const FEEDING_BEHAVIORS = ["GOED_GEGETEN", "ALLES_GEGETEN", "BIJNA_OP", "SLECHT_GEGETEN", "ONBEKEND"] as const;

export function PortalAnimalDetail({
  organizationUuid,
  dierUuid,
  onBack,
  onNavigate,
  onOpenAnimal,
}: {
  organizationUuid: string;
  dierUuid: string;
  onBack: () => void;
  onNavigate?: (page: Page) => void;
  /** Opens another of the foster's animals (Vorig / Volgend dier). */
  onOpenAnimal?: (organizationUuid: string, dierUuid: string) => void;
}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<PortalAnimalDetailData>({
    url: `portal/animals/${organizationUuid}/${dierUuid}`,
    method: "get",
  });
  const { mutate: saveMutation, isLoading: saving } = useCustomMutation();
  // Notitie-tags: de org-brede tag-catalogus voor de tag-kiezer in de notitie-dialoog.
  const noteTagsQuery = useCustom<string[]>({
    url: `portal/animals/${organizationUuid}/${dierUuid}/notes/tags`,
    method: "get",
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<{ name: string; features: string }>({ name: "", features: "" });
  const [saveError, setSaveError] = useState<string | undefined>();
  const { mutate: noteMutate, isLoading: noteSaving } = useCustomMutation();
  const [noteForm, setNoteForm] = useState<
    { id: number | null; title: string; text: string; pinned: boolean; tags: string[] } | null
  >(null);
  const [noteError, setNoteError] = useState<string | undefined>();
  // shared write state for the Bescherming / Voeding / Media tiles
  const { mutate: writeMutate, mutateAsync: writeMutateAsync, isLoading: writeSaving } = useCustomMutation();
  const [writeError, setWriteError] = useState<string | undefined>();
  const [vaccForm, setVaccForm] = useState<
    { id: string | null; name: string; brand: string; date: string; nextDate: string } | null
  >(null);
  // Bescherming-filterbalk (backoffice-tegel): type/status/zoeken.
  const [vaccTypeFilter, setVaccTypeFilter] = useState("alle");
  const [vaccStatusFilter, setVaccStatusFilter] = useState("alle");
  const [vaccSearch, setVaccSearch] = useState("");
  // AI-paspoortlezer in de enting-dialoog: geuploade pagina's + suggesties.
  const [aiPages, setAiPages] = useState<{ name: string; mediaType: string; data: string }[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | undefined>();
  const [aiSuggestions, setAiSuggestions] = useState<
    { type: string; brand: string; administrationDate: string; validTo: string; selected: boolean }[] | null
  >(null);
  const [feedingEditing, setFeedingEditing] = useState(false);
  const [feedingForm, setFeedingForm] = useState({
    feedType: "",
    brand: "",
    amountText: "",
    feedingTimes: "",
    dietStatus: "",
    note: "",
  });
  const [logForm, setLogForm] = useState<{ amountGrams: string; behavior: string; note: string } | null>(null);
  // Voedingslog-tabel: rij aanklikken selecteert; "Verwijderen" werkt op de selectie (backoffice-stijl).
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  // Gewicht toevoegen (federated write) — mirrors the Notities/Bescherming add form.
  const [weightForm, setWeightForm] = useState<{ kg: string; date: string } | null>(null);
  // Wizard-invulflow: starten levert een run mét schema op; de runner-modal rendert het formulier.
  const [activeWizard, setActiveWizard] = useState<{ run: PortalWizardRun; name: string } | null>(null);
  const [finishedWizard, setFinishedWizard] = useState<string | undefined>();
  // "Bekijk alle" per tile — collapsed tiles show the first few rows, like the mockup.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<DetailTab>("overview");
  // Acties-menu in the hero: closes on a click outside it or on Escape.
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!actionsOpen) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [actionsOpen]);
  // The foster's animals, for Vorig / Volgend dier — same order as the Mijn dieren list (newest first).
  const siblingsQuery = useCustom<PortalAnimals>({ url: "portal/animals", method: "get" });

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError || !data?.data) {
    return <FosterError onRetry={() => void refetch()} onNavigate={onNavigate} />;
  }

  const a = data.data;
  const age = ageLabel(a.dateOfBirth, t);

  // Collapsed tiles show the first ROW_LIMIT rows; the "Bekijk alle" footer (mockup) expands them.
  const ROW_LIMIT = 3;
  const isOpen = (key: string): boolean => !!expanded[key];
  const toggleOpen = (key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] }));
  const capped = <T,>(key: string, items: T[]): T[] => (isOpen(key) ? items : items.slice(0, ROW_LIMIT));
  const seeAllFooter = (key: string, count: number) =>
    count > ROW_LIMIT ? (
      <button className="fad-seeall" type="button" onClick={() => toggleOpen(key)}>
        {isOpen(key) ? t("portal.animalDetail.seeLess") : t("portal.animalDetail.seeAll")}
        <ChevronRightIcon />
      </button>
    ) : null;

  // Foster-visibility policy (backoffice-controlled, per organization). null = no policy yet → show all.
  // Hidden tiles/fields are simply not rendered; the platform also strips their values server-side.
  const tiles = a.tiles;
  const yesNo = (b: boolean | null): string | null =>
    b == null ? null : b ? t("portal.animalDetail.yes") : t("portal.animalDetail.no");
  // Levels (hidden|readonly|editable|deletable): a tile/field renders unless its level is "hidden".
  const tileVisible = (key: string): boolean => {
    const level = tiles?.[key]?.level;
    return level == null ? true : level !== "hidden";
  };
  const fieldVisible = (tileKey: string, fieldKey: string): boolean => {
    if (!tiles) return true;
    const tile = tiles[tileKey];
    if (tile == null) return true;
    if (tile.level === "hidden") return false;
    const fl = tile.fields?.[fieldKey];
    return fl == null ? true : fl !== "hidden";
  };
  const editable = (fieldKey: string): boolean => {
    const lvl = tiles?.BASIS?.fields?.[fieldKey];
    return lvl === "editable" || lvl === "deletable";
  };

  const MS_DAY = 86_400_000;
  const now = new Date();
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  // Verblijfduur in the hero: whole days since the placement started.
  const stayDays = (() => {
    if (!a.since) {
      return null;
    }
    const start = Date.parse(`${a.since.slice(0, 10)}T00:00:00`);
    return Number.isNaN(start) ? null : Math.max(0, Math.round((todayMs - start) / MS_DAY));
  })();

  // Bescherming — backoffice-status per record op basis van de Volgende-datum: Actueel (> 30 dagen),
  // Binnenkort (binnen 30 dagen), Verlopen (voorbij), Onbekend (geen datum).
  const vaccStatus = (v: PortalVaccination): "actueel" | "binnenkort" | "verlopen" | "onbekend" => {
    if (!v.nextDate) {
      return "onbekend";
    }
    const next = Date.parse(`${v.nextDate}T00:00:00`);
    if (Number.isNaN(next)) {
      return "onbekend";
    }
    if (next < todayMs) {
      return "verlopen";
    }
    if (next < todayMs + 30 * MS_DAY) {
      return "binnenkort";
    }
    return "actueel";
  };
  const vaccMatchesKind = (v: PortalVaccination, kind: string): boolean => {
    const txt = `${v.name ?? ""} ${v.type ?? ""}`.toLowerCase();
    if (kind === "enting") {
      return !txt.includes("ontworm") && !txt.includes("ontvlooi");
    }
    if (kind === "ontwormen") {
      return txt.includes("ontworm");
    }
    if (kind === "ontvlooien") {
      return txt.includes("ontvlooi");
    }
    return true;
  };
  const filteredVaccinations = a.vaccinations.filter((v) => {
    if (!vaccMatchesKind(v, vaccTypeFilter)) {
      return false;
    }
    if (vaccStatusFilter !== "alle" && vaccStatus(v) !== vaccStatusFilter) {
      return false;
    }
    if (vaccSearch.trim() !== "") {
      const q = vaccSearch.trim().toLowerCase();
      if (!`${v.name ?? ""} ${v.type ?? ""} ${v.brand ?? ""}`.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });
  const vaccFiltersActive = vaccTypeFilter !== "alle" || vaccStatusFilter !== "alle" || vaccSearch.trim() !== "";

  // Voedingslog — gedrag-pills zoals de backoffice-tegel ("Slecht gegeten" amber, "Goed gegeten" groen);
  // ruwe enum-waarden (SLECHT_GEGETEN) worden leesbaar gemaakt.
  const gedragLabel = (b: string | null): string => {
    if (!b) {
      return "—";
    }
    const clean = b.replace(/_/g, " ").toLowerCase();
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };
  const gedragTone = (b: string | null): "goed" | "slecht" | "neutraal" => {
    const v = (b ?? "").toLowerCase();
    if (v.includes("goed")) {
      return "goed";
    }
    if (v.includes("slecht") || v.includes("niet")) {
      return "slecht";
    }
    return "neutraal";
  };
  // edit (#3): the text fields naam + kenmerken get inline inputs (extend as more field types get editors);
  // a field is only editable when its policy level is editable/deletable.
  const basisEditable = editable("naam") || editable("kenmerken");
  const startEdit = () => {
    setSaveError(undefined);
    setForm({ name: a.name ?? "", features: a.features ?? "" });
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setSaveError(undefined);
  };
  const saveEdit = () => {
    setSaveError(undefined);
    const values: Record<string, unknown> = {};
    if (editable("naam")) values.name = form.name.trim() || null;
    if (editable("kenmerken")) values.features = form.features.trim() || null;
    saveMutation(
      { url: `portal/animals/${organizationUuid}/${dierUuid}`, method: "put", values },
      {
        onSuccess: () => {
          setEditing(false);
          void refetch();
        },
        onError: () => setSaveError(t("portal.animalDetail.edit.error")),
      },
    );
  };

  // Notities (rich animal_note) write — tile-level policy: editable → add/edit, deletable → also delete.
  const notesLevel = tiles?.NOTES?.level;
  const notesCanEdit = notesLevel === "editable" || notesLevel === "deletable";
  const notesCanDelete = notesLevel === "deletable";
  // Gewichten write — GEWICHTEN tile-level policy: editable/deletable → the foster may add a measurement.
  const weightsLevel = tiles?.GEWICHTEN?.level;
  const weightsCanEdit = weightsLevel === "editable" || weightsLevel === "deletable";
  const startAddNote = () => {
    setNoteError(undefined);
    setNoteForm({ id: null, title: "", text: "", pinned: false, tags: [] });
  };
  const startEditNote = (n: PortalRichNote) => {
    setNoteError(undefined);
    setNoteForm({ id: n.id, title: n.title ?? "", text: n.text ?? "", pinned: n.pinned, tags: [...n.tags] });
  };
  const cancelNote = () => {
    setNoteForm(null);
    setNoteError(undefined);
  };
  const saveNote = () => {
    if (noteForm == null) {
      return;
    }
    setNoteError(undefined);
    const isNew = noteForm.id === null;
    noteMutate(
      {
        url: isNew
          ? `portal/animals/${organizationUuid}/${dierUuid}/notes`
          : `portal/animals/${organizationUuid}/${dierUuid}/notes/${noteForm.id}`,
        method: isNew ? "post" : "put",
        values: {
          title: noteForm.title.trim() || null,
          text: noteForm.text.trim() || null,
          pinned: noteForm.pinned,
          tags: noteForm.tags,
        },
      },
      {
        onSuccess: () => {
          setNoteForm(null);
          void refetch();
        },
        onError: () => setNoteError(t("portal.animalDetail.edit.error")),
      },
    );
  };
  const removeNote = (id: number) => {
    if (!window.confirm(t("portal.animalDetail.notes.deleteConfirm"))) {
      return;
    }
    setNoteError(undefined);
    noteMutate(
      { url: `portal/animals/${organizationUuid}/${dierUuid}/notes/${id}`, method: "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setNoteError(t("portal.animalDetail.edit.error")),
      },
    );
  };
  /** Notitie-dialoog — de wire-ondersteunde backoffice-velden: Titel, Notitie, Vastgepind.
   *  (Datum, tags en opvolgdatum zet de organisatie in het backoffice; het portaal toont ze read-only.) */
  const renderNoteModal = () =>
    noteForm == null ? null : (
      <Modal
        open
        title={noteForm.id ? t("portal.animalDetail.edit.edit") : t("portal.animalDetail.notes.addNew")}
        onClose={cancelNote}
        footer={
          <>
            {noteError ? <span className="fad-error">{noteError}</span> : null}
            <Button variant="secondary" onClick={cancelNote} disabled={noteSaving}>
              {t("portal.animalDetail.edit.cancel")}
            </Button>
            <Button onClick={saveNote} disabled={noteSaving}>
              {noteSaving ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.edit.save")}
            </Button>
          </>
        }
      >
        <div className="fad-wizard-field">
          <label className="fad-feed-label">{t("portal.animalDetail.notes.titleLabel")}</label>
          <input
            className="fad-input"
            value={noteForm.title}
            onChange={(e) => setNoteForm((f) => (f ? { ...f, title: e.target.value } : f))}
          />
        </div>
        <div className="fad-wizard-field">
          <label className="fad-feed-label">{t("portal.animalDetail.notes.textLabel")}</label>
          <textarea
            className="fad-input fad-input--area"
            rows={5}
            value={noteForm.text}
            onChange={(e) => setNoteForm((f) => (f ? { ...f, text: e.target.value } : f))}
          />
        </div>
        <label className="fad-wizard-check">
          <input
            type="checkbox"
            checked={noteForm.pinned}
            onChange={(e) => setNoteForm((f) => (f ? { ...f, pinned: e.target.checked } : f))}
          />
          <span>{t("portal.animalDetail.notes.pinned")}</span>
        </label>
        {(() => {
          /* catalogus + eventuele tags op de notitie zelf (zodat bestaande selectie altijd toont) */
          const options = Array.from(new Set([...(noteTagsQuery.data?.data ?? []), ...noteForm.tags]));
          if (options.length === 0) {
            return null;
          }
          const toggle = (tag: string) =>
            setNoteForm((f) =>
              f
                ? {
                    ...f,
                    tags: f.tags.includes(tag) ? f.tags.filter((x) => x !== tag) : [...f.tags, tag],
                  }
                : f,
            );
          return (
            <div className="fad-wizard-field fad-note-tagpicker">
              <label className="fad-feed-label">{t("portal.animalDetail.notes.tagsLabel")}</label>
              <div className="fad-wizard-checks fad-wizard-checks--wrap">
                {options.map((tag) => (
                  <label key={tag} className="fad-wizard-check">
                    <input type="checkbox" checked={noteForm.tags.includes(tag)} onChange={() => toggle(tag)} />
                    <span>{tag}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>
    );

  // Bescherming / Voeding / Media writes — tile-level policy: editable → add/edit, deletable → also delete.
  const tileCanEdit = (key: string) => {
    const lvl = tiles?.[key]?.level;
    return lvl === "editable" || lvl === "deletable";
  };
  const tileCanDelete = (key: string) => tiles?.[key]?.level === "deletable";
  const base = `portal/animals/${organizationUuid}/${dierUuid}`;
  const doWrite = (
    url: string,
    method: "post" | "put" | "delete",
    values: Record<string, unknown>,
    onDone: () => void,
  ) => {
    setWriteError(undefined);
    writeMutate(
      { url, method, values },
      {
        onSuccess: () => {
          onDone();
          void refetch();
        },
        onError: () => setWriteError(t("portal.animalDetail.edit.error")),
      },
    );
  };
  // vaccinations
  const startAddVacc = (presetName?: string) => {
    setWriteError(undefined);
    setAiPages([]);
    setAiSuggestions(null);
    setAiError(undefined);
    setVaccForm({ id: null, name: presetName ?? "", brand: "", date: "", nextDate: "" });
  };
  // AI-paspoortlezer — pagina's inlezen (base64), suggesties ophalen en geselecteerde entingen importeren.
  const onAiFiles = (files: FileList | null) => {
    if (!files) {
      return;
    }
    setAiError(undefined);
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result ?? "");
        const base64 = result.includes(",") ? result.slice(result.indexOf(",") + 1) : result;
        setAiPages((p) =>
          p.length >= 12 ? p : [...p, { name: file.name, mediaType: file.type || "image/jpeg", data: base64 }],
        );
      };
      reader.readAsDataURL(file);
    });
  };
  const aiRead = async () => {
    setAiBusy(true);
    setAiError(undefined);
    setAiSuggestions(null);
    try {
      const res = await writeMutateAsync({
        url: `${base}/vaccinations/ai-suggest`,
        method: "post",
        values: { pages: aiPages.map(({ mediaType, data }) => ({ mediaType, data })) },
      });
      const body = res?.data as {
        vaccinations?: { type?: string; brand?: string; administrationDate?: string; validTo?: string }[];
        message?: string;
      } | null;
      const raw = body?.vaccinations ?? [];
      const list = raw.map((s) => ({
        type: s.type ?? "",
        brand: s.brand ?? "",
        administrationDate: s.administrationDate ?? "",
        validTo: s.validTo ?? "",
        selected: true,
      }));
      setAiSuggestions(list);
      if (list.length === 0) {
        // de asiel-AI stuurt een concrete melding mee (bv. "AI vond geen duidelijke vaccinaties")
        setAiError(body?.message?.trim() || t("portal.animalDetail.vacc.aiNone"));
      }
    } catch (e) {
      const status = (e as { statusCode?: number } | null)?.statusCode;
      setAiError(
        status === 503 ? t("portal.animalDetail.vacc.aiNotConfigured") : t("portal.animalDetail.vacc.aiError"),
      );
    } finally {
      setAiBusy(false);
    }
  };
  const aiImport = async () => {
    if (!aiSuggestions) {
      return;
    }
    setAiBusy(true);
    setAiError(undefined);
    try {
      for (const s of aiSuggestions.filter((x) => x.selected)) {
        await writeMutateAsync({
          url: `${base}/vaccinations`,
          method: "post",
          values: {
            name: s.type || s.brand || "Enting",
            brand: s.brand || null,
            date: s.administrationDate || null,
            nextDate: s.validTo || null,
          },
        });
      }
      setAiSuggestions(null);
      setAiPages([]);
      setVaccForm(null);
      void refetch();
    } catch {
      setAiError(t("portal.animalDetail.vacc.aiError"));
    } finally {
      setAiBusy(false);
    }
  };
  const startEditVacc = (v: PortalVaccination) => {
    setWriteError(undefined);
    setVaccForm({ id: v.id, name: v.name ?? "", brand: v.brand ?? "", date: v.date ?? "", nextDate: v.nextDate ?? "" });
  };
  const saveVacc = () => {
    if (vaccForm == null) {
      return;
    }
    const values = {
      name: vaccForm.name.trim() || null,
      brand: vaccForm.brand.trim() || null,
      date: vaccForm.date || null,
      nextDate: vaccForm.nextDate || null,
    };
    doWrite(
      vaccForm.id ? `${base}/vaccinations/${vaccForm.id}` : `${base}/vaccinations`,
      vaccForm.id ? "put" : "post",
      values,
      () => setVaccForm(null),
    );
  };
  const removeVacc = (id: string) => {
    if (window.confirm(t("portal.animalDetail.vacc.deleteConfirm"))) {
      doWrite(`${base}/vaccinations/${id}`, "delete", {}, () => undefined);
    }
  };
  // feeding schema
  const startEditFeeding = () => {
    setWriteError(undefined);
    const s = a.feeding?.schema;
    setFeedingForm({
      feedType: s?.feedType ?? "",
      brand: s?.brand ?? "",
      amountText: s?.amountText ?? "",
      feedingTimes: s?.feedingTimes ?? "",
      dietStatus: s?.dietStatus ?? "",
      note: s?.note ?? "",
    });
    setFeedingEditing(true);
  };
  const saveFeeding = () => {
    doWrite(
      `${base}/feeding/schema`,
      "put",
      {
        feedType: feedingForm.feedType.trim() || null,
        brand: feedingForm.brand.trim() || null,
        amountText: feedingForm.amountText.trim() || null,
        feedingTimes: feedingForm.feedingTimes.trim() || null,
        dietStatus: feedingForm.dietStatus.trim() || null,
        note: feedingForm.note.trim() || null,
      },
      () => setFeedingEditing(false),
    );
  };
  // feeding log
  const startAddLog = () => {
    setWriteError(undefined);
    setLogForm({ amountGrams: "", behavior: "", note: "" });
  };
  const saveLog = () => {
    if (logForm == null) {
      return;
    }
    const grams = logForm.amountGrams.trim() === "" ? null : Number(logForm.amountGrams);
    // hoeveelheid is verplicht (NOT NULL in de backoffice-voedingslogtabel)
    if (grams == null || Number.isNaN(grams) || grams <= 0) {
      setWriteError(t("portal.animalDetail.feeding.amountRequired"));
      return;
    }
    doWrite(
      `${base}/feeding/log`,
      "post",
      { amountGrams: grams, behavior: logForm.behavior.trim() || null, note: logForm.note.trim() || null },
      () => setLogForm(null),
    );
  };
  const removeLog = (id: number) => {
    if (window.confirm(t("portal.animalDetail.feeding.logDeleteConfirm"))) {
      doWrite(`${base}/feeding/log/${id}`, "delete", {}, () => setSelectedLogId(null));
    }
  };
  // gewichten — meting toevoegen (backoffice-dialoog: Datum + Gewicht (kg))
  const startAddWeight = () => {
    setWriteError(undefined);
    setWeightForm({ kg: "", date: todayIso() });
  };
  const saveWeight = () => {
    if (weightForm == null) {
      return;
    }
    // The backoffice rejects kg ≤ 0; don't even POST an empty/invalid value.
    const kg = Number(weightForm.kg.replace(",", "."));
    if (weightForm.kg.trim() === "" || !Number.isFinite(kg) || kg <= 0) {
      setWriteError(t("portal.animalDetail.edit.error"));
      return;
    }
    doWrite(`${base}/weights`, "post", { kg, date: weightForm.date || null }, () => setWeightForm(null));
  };
  // wizards — starten levert de run (mét schema) op; de runner-modal doet PATCH per stap + complete.
  const startWizard = (w: PortalWizardOption) => {
    setWriteError(undefined);
    setFinishedWizard(undefined);
    writeMutate(
      { url: `${base}/wizards`, method: "post", values: { wizardType: w.wizardType } },
      {
        onSuccess: (res) => {
          const run = (res?.data ?? null) as PortalWizardRun | null;
          if (run?.id) {
            setActiveWizard({ run, name: w.name || w.wizardType });
          } else {
            setWriteError(t("portal.animalDetail.edit.error"));
          }
        },
        onError: () => setWriteError(t("portal.animalDetail.edit.error")),
      },
    );
  };
  const writeActions = (onSave: () => void, onCancel: () => void) => (
    <div className="fad-edit-actions">
      {writeError ? <span className="fad-error">{writeError}</span> : null}
      <button className="fad-btn fad-btn--ghost" type="button" onClick={onCancel} disabled={writeSaving}>
        {t("portal.animalDetail.edit.cancel")}
      </button>
      <button className="fad-btn fad-btn--primary" type="button" onClick={onSave} disabled={writeSaving}>
        {writeSaving ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.edit.save")}
      </button>
    </div>
  );
  /** Enting-dialoog (backoffice-velden: Type, Product/Merk, Gegeven op, Volgende op) + AI-paspoortlezer
   *  bij nieuwe entingen: upload paspoortpagina's → "Lees alle entingen (AI)" → suggesties importeren. */
  const renderVaccModal = () =>
    vaccForm == null ? null : (
      <Modal
        open
        title={vaccForm.id ? t("portal.animalDetail.edit.edit") : t("portal.animalDetail.vacc.add")}
        onClose={() => setVaccForm(null)}
        footer={
          <>
            {writeError ? <span className="fad-error">{writeError}</span> : null}
            <Button variant="secondary" onClick={() => setVaccForm(null)} disabled={writeSaving}>
              {t("portal.animalDetail.edit.cancel")}
            </Button>
            <Button onClick={saveVacc} disabled={writeSaving}>
              {writeSaving ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.edit.save")}
            </Button>
          </>
        }
      >
        <div className="fad-wizard-field">
          <label className="fad-feed-label">{t("portal.animalDetail.vacc.name")}</label>
          <input
            className="fad-input"
            value={vaccForm.name}
            onChange={(e) => setVaccForm((f) => (f ? { ...f, name: e.target.value } : f))}
          />
        </div>
        <div className="fad-wizard-field">
          <label className="fad-feed-label">{t("portal.animalDetail.vacc.brand")}</label>
          <input
            className="fad-input"
            value={vaccForm.brand}
            onChange={(e) => setVaccForm((f) => (f ? { ...f, brand: e.target.value } : f))}
          />
        </div>
        <div className="fad-form-row">
          <label className="fad-form-field">
            <span>{t("portal.animalDetail.vacc.date")}</span>
            <input
              className="fad-input"
              type="date"
              value={vaccForm.date}
              onChange={(e) => setVaccForm((f) => (f ? { ...f, date: e.target.value } : f))}
            />
          </label>
          <label className="fad-form-field">
            <span>{t("portal.animalDetail.vacc.nextDate")}</span>
            <input
              className="fad-input"
              type="date"
              value={vaccForm.nextDate}
              onChange={(e) => setVaccForm((f) => (f ? { ...f, nextDate: e.target.value } : f))}
            />
          </label>
        </div>

        {vaccForm.id === null ? (
          <div className="fad-ai-box">
            <div className="fad-feed-sub">{t("portal.animalDetail.vacc.aiTitle")}</div>
            <p className="fad-ai-hint">{t("portal.animalDetail.vacc.aiHint")}</p>
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={(e) => {
                onAiFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {aiPages.length > 0 ? (
              <ul className="fad-ai-pages">
                {aiPages.map((p, i) => (
                  <li key={`${p.name}-${i}`}>
                    <span>{p.name}</span>
                    <button
                      className="fad-icon-btn fad-icon-btn--danger"
                      type="button"
                      onClick={() => setAiPages((pages) => pages.filter((_, idx) => idx !== i))}
                    >
                      <TrashIcon />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="fad-feed-actions">
              <button
                className="fad-btn fad-btn--primary"
                type="button"
                disabled={aiPages.length === 0 || aiBusy}
                onClick={() => void aiRead()}
              >
                {aiBusy ? t("portal.animalDetail.vacc.aiBusy") : t("portal.animalDetail.vacc.aiRead")}
              </button>
              {aiPages.length > 0 ? (
                <button
                  className="fad-btn fad-btn--ghost"
                  type="button"
                  disabled={aiBusy}
                  onClick={() => {
                    setAiPages([]);
                    setAiSuggestions(null);
                  }}
                >
                  {t("portal.animalDetail.vacc.aiClear")}
                </button>
              ) : null}
            </div>
            {aiError ? (
              <p className="fad-error" role="alert">
                {aiError}
              </p>
            ) : null}
            {aiSuggestions && aiSuggestions.length > 0 ? (
              <>
                <p className="fad-ai-hint">
                  {t("portal.animalDetail.vacc.aiFoundPrefix")} {aiSuggestions.length}{" "}
                  {t("portal.animalDetail.vacc.aiFoundSuffix")}
                </p>
                <ul className="fad-ai-suggestions">
                  {aiSuggestions.map((s, i) => (
                    <li key={i}>
                      <label className="fad-wizard-check">
                        <input
                          type="checkbox"
                          checked={s.selected}
                          onChange={() =>
                            setAiSuggestions((list) =>
                              list
                                ? list.map((x, idx) => (idx === i ? { ...x, selected: !x.selected } : x))
                                : list,
                            )
                          }
                        />
                        <span>
                          <strong>{s.type || s.brand || "—"}</strong>
                          {s.brand && s.type ? ` · ${s.brand}` : ""}
                          {s.administrationDate ? ` · ${s.administrationDate}` : ""}
                          {s.validTo ? ` → ${s.validTo}` : ""}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <button
                  className="fad-btn fad-btn--primary"
                  type="button"
                  disabled={aiBusy || aiSuggestions.every((s) => !s.selected)}
                  onClick={() => void aiImport()}
                >
                  {aiBusy ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.vacc.aiImport")}
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </Modal>
    );

  // Bescherming — entingen/ontwormingen/ontvlooiingen (federated)
  const protectionTile = tileVisible("INENTINGEN") ? (
      <Tile
        icon={<ShieldIcon width={22} height={22} />}
        title={t("portal.animalDetail.tile.protection")}
        footer={seeAllFooter("INENTINGEN", filteredVaccinations.length)}
      >
        {a.vaccinations.length > 0 ? (
          /* backoffice-filterbalk: Type / Status / Zoeken + wissen */
          <div className="fad-vacc-filters">
            <select className="fad-input" value={vaccTypeFilter} onChange={(e) => setVaccTypeFilter(e.target.value)}>
              <option value="alle">{t("portal.animalDetail.vacc.filter.all")}</option>
              <option value="enting">{t("portal.animalDetail.vacc.filter.enting")}</option>
              <option value="ontwormen">{t("portal.animalDetail.vacc.filter.ontwormen")}</option>
              <option value="ontvlooien">{t("portal.animalDetail.vacc.filter.ontvlooien")}</option>
            </select>
            <select
              className="fad-input"
              value={vaccStatusFilter}
              onChange={(e) => setVaccStatusFilter(e.target.value)}
            >
              <option value="alle">{t("portal.animalDetail.vacc.filter.all")}</option>
              <option value="actueel">{t("portal.animalDetail.protection.status.actueel")}</option>
              <option value="binnenkort">{t("portal.animalDetail.protection.status.binnenkort")}</option>
              <option value="verlopen">{t("portal.animalDetail.protection.status.verlopen")}</option>
              <option value="onbekend">{t("portal.animalDetail.protection.status.onbekend")}</option>
            </select>
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.vacc.filter.search")}
              value={vaccSearch}
              onChange={(e) => setVaccSearch(e.target.value)}
            />
            {vaccFiltersActive ? (
              <button
                className="fad-seeall"
                type="button"
                onClick={() => {
                  setVaccTypeFilter("alle");
                  setVaccStatusFilter("alle");
                  setVaccSearch("");
                }}
              >
                {t("portal.animalDetail.vacc.filter.clear")}
              </button>
            ) : null}
          </div>
        ) : null}

        {a.vaccinations.length === 0 ? (
          <p className="fad-empty">{t("portal.animalDetail.protection.empty")}</p>
        ) : filteredVaccinations.length === 0 ? (
          <p className="fad-empty">{t("portal.animalDetail.vacc.filter.none")}</p>
        ) : (
          <ul className="fad-list">
            {capped("INENTINGEN", filteredVaccinations).map((v: PortalVaccination) => (
              <li key={v.id} className="fad-row">
                <div className="fad-row-main">
                  <strong>{v.name || v.type || "—"}</strong>
                  {v.brand ? <span className="fad-row-meta">{v.brand}</span> : null}
                  <span className="fad-row-meta">
                    {v.date ? formatDateShort(v.date, locale) : "—"}
                    {v.nextDate
                      ? ` · ${t("portal.animalDetail.protection.next")} ${formatDateShort(v.nextDate, locale)}`
                      : ""}
                    {v.givenBy ? ` · ${t("portal.animalDetail.protection.givenBy")} ${v.givenBy}` : ""}
                  </span>
                </div>
                <div className="fad-row-side">
                  <span className={`fad-status fad-status--${vaccStatus(v)}`}>
                    {t(`portal.animalDetail.protection.status.${vaccStatus(v)}`)}
                  </span>
                  {tileCanEdit("INENTINGEN") ? (
                    <button
                      className="fad-icon-btn"
                      type="button"
                      title={t("portal.animalDetail.edit.edit")}
                      onClick={() => startEditVacc(v)}
                    >
                      <PencilIcon />
                    </button>
                  ) : null}
                  {tileCanDelete("INENTINGEN") ? (
                    <button
                      className="fad-icon-btn fad-icon-btn--danger"
                      type="button"
                      title={t("portal.animalDetail.vacc.delete")}
                      onClick={() => v.id != null && removeVacc(v.id)}
                    >
                      <TrashIcon />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {tileCanEdit("INENTINGEN") ? (
          <div className="fad-feed-actions">
            <button className="fad-btn fad-btn--primary" type="button" onClick={() => startAddVacc()}>
              <PlusIcon /> {t("portal.animalDetail.vacc.addEnting")}
            </button>
            <button
              className="fad-btn fad-btn--ghost"
              type="button"
              onClick={() => startAddVacc(t("portal.animalDetail.vacc.parasitePreset"))}
            >
              <PlusIcon /> {t("portal.animalDetail.vacc.addParasite")}
            </button>
          </div>
        ) : null}
      </Tile>
    ) : null;

  // Notities — the rich animal_note tile (federated; distinct from the recent_notes change-log)
  const notesTile = tileVisible("NOTES") ? (
      <Tile
        icon={<NoteIcon />}
        title={t("portal.animalDetail.tile.notes")}
        footer={seeAllFooter("NOTES", a.richNotes.length)}
      >
        {a.richNotes.length === 0 ? (
          <p className="fad-empty">{t("portal.animalDetail.notes.empty")}</p>
        ) : (
          <ul className="fad-list">
            {capped(
              "NOTES",
              /* backoffice-sortering: vastgepind eerst, dan nieuwste eerst */
              [...a.richNotes].sort((x, y) => {
                if (x.pinned !== y.pinned) {
                  return x.pinned ? -1 : 1;
                }
                return (y.date ?? "").localeCompare(x.date ?? "");
              }),
            ).map((n: PortalRichNote) => (
              <li key={n.id} className="fad-row">
                <div className="fad-row-main">
                  <span className="fad-note-head">
                    {n.date ? <span className="fad-row-meta">{formatDateShort(n.date, locale)}</span> : null}
                    {n.title ? <strong>{n.title}</strong> : null}
                    {n.pinned ? (
                      <span className="fad-status fad-status--binnenkort">
                        {t("portal.animalDetail.notes.pinned")}
                      </span>
                    ) : null}
                  </span>
                  {n.text ? <span className="fad-note-text">{n.text}</span> : null}
                  {n.tags.length > 0 ? (
                    <span className="fad-tags">
                      {n.tags.map((tag) => (
                        <span key={tag} className="fad-tag">
                          {tag}
                        </span>
                      ))}
                    </span>
                  ) : null}
                  {n.author ? (
                    <span className="fad-row-meta">
                      {t("portal.animalDetail.protection.givenBy")} {n.author}
                    </span>
                  ) : null}
                </div>
                {notesCanEdit ? (
                  <div className="fad-row-side">
                    <button
                      className="fad-icon-btn"
                      type="button"
                      title={t("portal.animalDetail.edit.edit")}
                      onClick={() => startEditNote(n)}
                    >
                      <PencilIcon />
                    </button>
                    {notesCanDelete ? (
                      <button
                        className="fad-icon-btn fad-icon-btn--danger"
                        type="button"
                        title={t("portal.animalDetail.notes.delete")}
                        onClick={() => n.id != null && removeNote(n.id)}
                      >
                        <TrashIcon />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {notesCanEdit ? (
          <div className="fad-feed-actions">
            <button className="fad-btn fad-btn--primary" type="button" onClick={startAddNote}>
              <PlusIcon /> {t("portal.animalDetail.notes.addNew")}
            </button>
          </div>
        ) : null}
      </Tile>
    ) : null;

  // Gewichten — federated (recent weights) with add-measurement (mirrors the Notities add path)
  const weightsTile = tileVisible("GEWICHTEN") ? (
      <Tile
        icon={<ScaleIcon />}
        title={t("portal.animalDetail.tile.weights")}
        footer={seeAllFooter("GEWICHTEN", a.recentWeights.length)}
      >
        {a.recentWeights.length === 0 ? (
          <p className="fad-empty">{t("portal.animalDetail.weights.empty")}</p>
        ) : (
          <>
            {/* backoffice-tegel: laatste-meting-regel + grafiek + tabel */}
            <p className="fad-weight-latest">
              {t("portal.animalDetail.weights.latest")}{" "}
              <strong>
                {a.recentWeights[0].measuredAt
                  ? formatDateShort(a.recentWeights[0].measuredAt, locale)
                  : "—"}
                {" - "}
                {formatWeight(a.recentWeights[0].kg, locale)} {t("portal.animalDetail.weights.unit")}
              </strong>
            </p>
            <WeightChart
              weights={a.recentWeights}
              labels={{
                m3: t("portal.animalDetail.weights.period3m"),
                m1: t("portal.animalDetail.weights.period1m"),
                all: t("portal.animalDetail.weights.periodAll"),
                few: t("portal.animalDetail.weights.periodFew"),
              }}
            />
            <div className="fad-feedlog-box">
              <table className="fad-feedlog-table">
                <thead>
                  <tr>
                    <th>{t("portal.animalDetail.feeding.column.date")}</th>
                    <th>{t("portal.animalDetail.weights.columnKg")}</th>
                    <th>{t("portal.animalDetail.weights.columnSource")}</th>
                  </tr>
                </thead>
                <tbody>
                  {capped("GEWICHTEN", a.recentWeights).map((w: PortalWeight) => (
                    <tr key={w.id}>
                      <td>{w.measuredAt ? formatDateShort(w.measuredAt, locale) : "—"}</td>
                      <td>{formatWeight(w.kg, locale)}</td>
                      <td className="fad-row-meta">{sourceLabel(w.source, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {weightsCanEdit ? (
          <div className="fad-feed-actions">
            <button className="fad-btn fad-btn--primary" type="button" onClick={startAddWeight}>
              <PlusIcon /> {t("portal.animalDetail.weights.add")}
            </button>
          </div>
        ) : null}
      </Tile>
    ) : null;

  // Voeding — federated (plan + log) with schema-edit + log add/delete
  const feedingTile = tileVisible("VOEDING") ? (
      <Tile
        icon={<BowlIcon />}
        title={t("portal.animalDetail.tile.feeding")}
        footer={seeAllFooter("VOEDING", a.feeding?.log.length ?? 0)}
      >
        {feedingEditing ? (
          <div className="fad-note-form">
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.feeding.feedType")}
              value={feedingForm.feedType}
              onChange={(e) => setFeedingForm((f) => ({ ...f, feedType: e.target.value }))}
            />
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.feeding.brand")}
              value={feedingForm.brand}
              onChange={(e) => setFeedingForm((f) => ({ ...f, brand: e.target.value }))}
            />
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.feeding.amount")}
              value={feedingForm.amountText}
              onChange={(e) => setFeedingForm((f) => ({ ...f, amountText: e.target.value }))}
            />
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.feeding.times")}
              value={feedingForm.feedingTimes}
              onChange={(e) => setFeedingForm((f) => ({ ...f, feedingTimes: e.target.value }))}
            />
            <input
              className="fad-input"
              placeholder={t("portal.animalDetail.feeding.diet")}
              value={feedingForm.dietStatus}
              onChange={(e) => setFeedingForm((f) => ({ ...f, dietStatus: e.target.value }))}
            />
            <textarea
              className="fad-input fad-input--area"
              rows={2}
              placeholder={t("portal.animalDetail.feeding.note")}
              value={feedingForm.note}
              onChange={(e) => setFeedingForm((f) => ({ ...f, note: e.target.value }))}
            />
            {writeActions(saveFeeding, () => setFeedingEditing(false))}
          </div>
        ) : a.feeding?.schema ? (
          /* backoffice-tegel: het schema als omrand grid met binnenranden, Opmerking als volle rij */
          <div className="fad-feed-box">
            <div className="fad-feed-cell">
              <span className="fad-feed-label">{t("portal.animalDetail.feeding.feedType")}</span>
              <span className="fad-feed-value">{a.feeding.schema.feedType || "-"}</span>
            </div>
            <div className="fad-feed-cell">
              <span className="fad-feed-label">{t("portal.animalDetail.feeding.brand")}</span>
              <span className="fad-feed-value">{a.feeding.schema.brand || "-"}</span>
            </div>
            <div className="fad-feed-cell">
              <span className="fad-feed-label">{t("portal.animalDetail.feeding.amount")}</span>
              <span className="fad-feed-value">{a.feeding.schema.amountText || "-"}</span>
            </div>
            <div className="fad-feed-cell">
              <span className="fad-feed-label">{t("portal.animalDetail.feeding.times")}</span>
              <span className="fad-feed-value">{a.feeding.schema.feedingTimes || "-"}</span>
            </div>
            {a.feeding.schema.dietStatus ? (
              <div className="fad-feed-cell">
                <span className="fad-feed-label">{t("portal.animalDetail.feeding.diet")}</span>
                <span className="fad-feed-value">{a.feeding.schema.dietStatus}</span>
              </div>
            ) : null}
            <div className="fad-feed-cell fad-feed-cell--wide">
              <span className="fad-feed-label">{t("portal.animalDetail.feeding.note")}</span>
              <span className="fad-feed-value">{a.feeding.schema.note || "-"}</span>
            </div>
          </div>
        ) : null}

        {logForm ? (
          <div className="fad-note-form">
            <input
              className="fad-input"
              type="number"
              placeholder={t("portal.animalDetail.feeding.amountGrams")}
              value={logForm.amountGrams}
              onChange={(e) => setLogForm((f) => (f ? { ...f, amountGrams: e.target.value } : f))}
            />
            {/* Gedrag: zelfde keuzelijst als de backoffice-voedingsdialoog (enum-codes) */}
            <select
              className="fad-input"
              aria-label={t("portal.animalDetail.feeding.behavior")}
              value={logForm.behavior}
              onChange={(e) => setLogForm((f) => (f ? { ...f, behavior: e.target.value } : f))}
            >
              <option value="">{t("portal.animalDetail.feeding.behaviorPlaceholder")}</option>
              {FEEDING_BEHAVIORS.map((code) => (
                <option key={code} value={code}>
                  {gedragLabel(code)}
                </option>
              ))}
            </select>
            <textarea
              className="fad-input fad-input--area"
              rows={2}
              placeholder={t("portal.animalDetail.feeding.note")}
              value={logForm.note}
              onChange={(e) => setLogForm((f) => (f ? { ...f, note: e.target.value } : f))}
            />
            {writeActions(saveLog, () => setLogForm(null))}
          </div>
        ) : null}

        {a.feeding && a.feeding.log.length > 0 ? (
          /* backoffice-tegel: "Voedingslog" — samenvattingsregel van de laatste voeding + tabel met
             gedrag-pills; een rij aanklikken selecteert hem voor de Verwijderen-knop. */
          <>
            <h3 className="fad-feed-sub">{t("portal.animalDetail.feeding.log")}</h3>
            <div className="fad-feedlog-box">
              <div className="fad-feedlog-summary">
                {a.feeding.log[0].fedAt ? formatDateShort(a.feeding.log[0].fedAt, locale) : "—"}
                {" - "}
                {gedragLabel(a.feeding.log[0].behavior)}
              </div>
              <table className="fad-feedlog-table">
                <thead>
                  <tr>
                    <th>{t("portal.animalDetail.feeding.column.date")}</th>
                    <th>{t("portal.animalDetail.feeding.amount")}</th>
                    <th>{t("portal.animalDetail.feeding.behavior")}</th>
                  </tr>
                </thead>
                <tbody>
                  {capped("VOEDING", a.feeding.log).map((e) => (
                    <tr
                      key={e.id}
                      className={selectedLogId === e.id ? "is-selected" : ""}
                      title={e.note ?? undefined}
                      onClick={() =>
                        tileCanDelete("VOEDING") &&
                        setSelectedLogId((cur) => (cur === e.id ? null : (e.id ?? null)))
                      }
                    >
                      <td>{e.fedAt ? formatDateShort(e.fedAt, locale) : "—"}</td>
                      <td>{e.amountGrams != null ? `${e.amountGrams}g` : "—"}</td>
                      <td>
                        {e.behavior ? (
                          <span className={`fad-gedrag fad-gedrag--${gedragTone(e.behavior)}`}>
                            {gedragLabel(e.behavior)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {!feedingEditing && logForm === null && (tileCanEdit("VOEDING") || tileCanDelete("VOEDING")) ? (
          <div className="fad-feed-actions">
            {tileCanEdit("VOEDING") ? (
              <button className="fad-btn fad-btn--primary" type="button" onClick={startAddLog}>
                <PlusIcon /> {t("portal.animalDetail.feeding.addLog")}
              </button>
            ) : null}
            {tileCanDelete("VOEDING") ? (
              <button
                className="fad-btn fad-btn--danger"
                type="button"
                disabled={selectedLogId == null || writeSaving}
                onClick={() => selectedLogId != null && removeLog(selectedLogId)}
              >
                {t("portal.animalDetail.feeding.logDelete")}
              </button>
            ) : null}
            {tileCanEdit("VOEDING") ? (
              <button className="fad-btn fad-btn--ghost" type="button" onClick={startEditFeeding}>
                {t("portal.animalDetail.feeding.editSchema")}
              </button>
            ) : null}
          </div>
        ) : null}

        {!feedingEditing &&
        logForm === null &&
        (!a.feeding || (a.feeding.schema == null && a.feeding.log.length === 0)) ? (
          <p className="fad-empty">{t("portal.animalDetail.feeding.empty")}</p>
        ) : null}
      </Tile>
    ) : null;

  // Registraties & Wizards — available wizards to start (proposed contract; empty until backoffice ships it)
  const wizardsTile = tileVisible("WIZARDS") ? (
      <Tile
        icon={<WizardIcon />}
        title={t("portal.animalDetail.tile.registrations")}
        footer={seeAllFooter("WIZARDS", a.wizards.length)}
      >
        {a.wizards.length === 0 ? (
          <p className="fad-empty">{t("portal.animalDetail.wizards.empty")}</p>
        ) : (
          <ul className="fad-list">
            {capped("WIZARDS", a.wizards).map((w: PortalWizardOption) => (
              <li key={w.wizardType} className="fad-row">
                <div className="fad-row-main">
                  <strong>{w.name || w.wizardType}</strong>
                  {w.description ? <span className="fad-note-text">{w.description}</span> : null}
                </div>
                {tileCanEdit("WIZARDS") ? (
                  <div className="fad-row-side">
                    {finishedWizard === w.wizardType ? (
                      <span className="fad-wizard-started">{t("portal.animalDetail.wizards.finished2")}</span>
                    ) : null}
                    <button
                      className="fad-btn-start"
                      type="button"
                      onClick={() => startWizard(w)}
                      disabled={writeSaving}
                    >
                      <PlayIcon /> {t("portal.animalDetail.wizards.start")}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {writeError ? <span className="fad-error">{writeError}</span> : null}
      </Tile>
    ) : null;

  // ── Overzicht: compact summary tiles (full versions live on the other tabs) ──────────────────
  const infoRow = (key: string, label: string, value: ReactNode) => (
    <div className="fad-info-row" key={key}>
      <span className="fad-info-label">{label}</span>
      <span className="fad-info-value">{value}</span>
    </div>
  );

  // Basisinformatie lives in the hero's right column, under Acties: only the fields the hero does not already
  // show (species, breed, sex and date of birth are in the header), each behind its BASIS field toggle.
  // Optional numbers/dates only show when filled in; Bewerken turns naam + kenmerken into inputs in place.
  const heroFacts: { key: string; label: string; value: ReactNode }[] = !tileVisible("BASIS")
    ? []
    : [
        ...(editing && editable("naam")
          ? [
              {
                key: "naam",
                label: t("portal.animalDetail.field.name"),
                value: (
                  <input
                    className="fad-input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                ),
              },
            ]
          : []),
        ...(fieldVisible("BASIS", "kruising")
          ? [{ key: "kruising", label: t("portal.animalDetail.field.crossbreed"), value: yesNo(a.crossbreed) ?? "—" }]
          : []),
        ...(fieldVisible("BASIS", "gecastreerd")
          ? [{ key: "gecastreerd", label: t("portal.animalDetail.field.caster"), value: yesNo(a.neutered) ?? "—" }]
          : []),
        ...(fieldVisible("BASIS", "castratieDatum") && a.neuteredDate
          ? [
              {
                key: "castratiedatum",
                label: t("portal.animalDetail.field.castrationDate"),
                value: formatDateLong(a.neuteredDate, locale),
              },
            ]
          : []),
        ...(fieldVisible("BASIS", "chipPasnummer") && a.chipNumber
          ? [{ key: "chip", label: t("portal.animalDetail.field.chip"), value: a.chipNumber }]
          : []),
        ...(fieldVisible("BASIS", "paspoortNummer") && a.passportNumber
          ? [{ key: "paspoort", label: t("portal.animalDetail.field.passport"), value: a.passportNumber }]
          : []),
        ...(fieldVisible("BASIS", "stamboomNummer") && a.pedigreeNumber
          ? [{ key: "stamboom", label: t("portal.animalDetail.field.pedigree"), value: a.pedigreeNumber }]
          : []),
        ...(fieldVisible("BASIS", "overledendatum") && a.deceasedDate
          ? [{ key: "overleden", label: t("portal.animalDetail.field.deceased"), value: formatDateLong(a.deceasedDate, locale) }]
          : []),
        ...(fieldVisible("BASIS", "kenmerken") && (a.features || (editing && editable("kenmerken")))
          ? [
              {
                key: "kenmerken",
                label: t("portal.animalDetail.field.features"),
                value:
                  editing && editable("kenmerken") ? (
                    <textarea
                      className="fad-input fad-input--area"
                      rows={3}
                      value={form.features}
                      onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
                    />
                  ) : (
                    a.features
                  ),
              },
            ]
          : []),
      ];
  const heroCanEdit = tileVisible("BASIS") && basisEditable;

  // Gewicht — chart + the latest measurement (recentWeights[0] is the newest).
  const latestWeight = a.recentWeights[0] ?? null;
  const weightOverview = tileVisible("GEWICHTEN") ? (
    <Tile icon={<ScaleIcon />} title={t("portal.animalDetail.overview.weight")}>
      {latestWeight == null ? (
        <p className="fad-empty">{t("portal.animalDetail.weights.empty")}</p>
      ) : (
        <>
          <WeightChart
            weights={a.recentWeights}
            labels={{
              m3: t("portal.animalDetail.weights.period3m"),
              m1: t("portal.animalDetail.weights.period1m"),
              all: t("portal.animalDetail.weights.periodAll"),
              few: t("portal.animalDetail.weights.periodFew"),
            }}
          />
          <div className="fad-weight-summary">
            <ScaleIcon className="fad-weight-summary-icon" />
            <div>
              <span>{t("portal.animalDetail.overview.latestMeasurement")}</span>
              <strong>{latestWeight.measuredAt ? formatDateLong(latestWeight.measuredAt, locale) : "—"}</strong>
            </div>
            <div>
              <span>{t("portal.animalDetail.overview.weightValue")}</span>
              <strong>
                {formatWeight(latestWeight.kg, locale)} {t("portal.animalDetail.weights.unit")}
              </strong>
            </div>
          </div>
        </>
      )}
    </Tile>
  ) : null;

  // Voeding — the feeding plan as label/value rows; "Bewerken" opens the plan editor on the Verzorging tab.
  const schema = a.feeding?.schema ?? null;
  const feedingOverview = tileVisible("VOEDING") ? (
    <Tile
      icon={<BowlIcon />}
      title={t("portal.animalDetail.tile.feeding")}
      action={
        tileCanEdit("VOEDING") ? (
          <button
            className="fad-edit-btn"
            type="button"
            onClick={() => {
              setTab("care");
              startEditFeeding();
            }}
          >
            <PencilIcon /> {t("portal.animalDetail.edit.edit")}
          </button>
        ) : null
      }
    >
      {schema ? (
        <div className="fad-info-table">
          {infoRow("type", t("portal.animalDetail.feeding.feedType"), schema.feedType || "—")}
          {infoRow("merk", t("portal.animalDetail.feeding.brand"), schema.brand || "—")}
          {infoRow("hoeveelheid", t("portal.animalDetail.feeding.amount"), schema.amountText || "—")}
          {infoRow("tijden", t("portal.animalDetail.feeding.times"), schema.feedingTimes || "—")}
          {schema.dietStatus ? infoRow("dieet", t("portal.animalDetail.feeding.diet"), schema.dietStatus) : null}
          {schema.note ? infoRow("opmerking", t("portal.animalDetail.feeding.note"), schema.note) : null}
        </div>
      ) : (
        <p className="fad-empty">{t("portal.animalDetail.feeding.empty")}</p>
      )}
    </Tile>
  ) : null;

  // Medische aandachtspunten — what comes up next first (upcoming repeats, soonest first), then the rest.
  const medicalRank = (v: PortalVaccination): number => {
    const st = vaccStatus(v);
    return st === "binnenkort" ? 0 : st === "actueel" ? 1 : st === "verlopen" ? 2 : 3;
  };
  const medicalItems = [...a.vaccinations]
    .sort((x, y) => medicalRank(x) - medicalRank(y) || (x.nextDate ?? "").localeCompare(y.nextDate ?? ""))
    .slice(0, 3);
  const isParasiteTreatment = (v: PortalVaccination) => {
    const txt = `${v.name ?? ""} ${v.type ?? ""}`.toLowerCase();
    return txt.includes("ontworm") || txt.includes("ontvlooi");
  };
  const medicalOverview = tileVisible("INENTINGEN") ? (
    <Tile
      icon={<StethoscopeIcon />}
      title={t("portal.animalDetail.overview.medical")}
      action={
        a.vaccinations.length > 0 ? (
          <button className="fad-seeall" type="button" onClick={() => setTab("medical")}>
            {t("portal.animalDetail.overview.seeAllMedical")}
            <ChevronRightIcon />
          </button>
        ) : null
      }
    >
      {medicalItems.length === 0 ? (
        <p className="fad-empty">{t("portal.animalDetail.overview.medicalEmpty")}</p>
      ) : (
        <ul className="fad-med-list">
          {medicalItems.map((v) => (
            <li key={v.id ?? `${v.name}-${v.date}`} className="fad-med-item">
              <span className="fad-med-icon">{isParasiteTreatment(v) ? <PillIcon /> : <SyringeIcon />}</span>
              <span className="fad-med-main">
                <strong>{v.name || v.type || "—"}</strong>
                <span className="fad-med-meta">
                  {v.date ? (
                    <span>
                      {t("portal.animalDetail.overview.last")} {formatDateShort(v.date, locale)}
                    </span>
                  ) : null}
                  {v.nextDate ? (
                    <span>
                      {t("portal.animalDetail.protection.next")} {formatDateShort(v.nextDate, locale)}
                    </span>
                  ) : null}
                </span>
              </span>
              <span className={`fad-status fad-status--${vaccStatus(v)}`}>
                {t(`portal.animalDetail.protection.status.${vaccStatus(v)}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  ) : null;

  // Recente notities — the three newest (pinned or not), with a "Nieuwe notitie" button when allowed.
  const recentNotes = [...a.richNotes].sort((x, y) => (y.date ?? "").localeCompare(x.date ?? "")).slice(0, 3);
  const notesOverview = tileVisible("NOTES") ? (
    <Tile
      icon={<NoteIcon />}
      title={t("portal.animalDetail.overview.recentNotes")}
      action={
        a.richNotes.length > 0 ? (
          <button className="fad-seeall" type="button" onClick={() => setTab("notes")}>
            {t("portal.animalDetail.overview.seeAllNotes")}
            <ChevronRightIcon />
          </button>
        ) : null
      }
      footer={
        notesCanEdit ? (
          <button className="fad-btn fad-btn--ghost fad-btn--block" type="button" onClick={startAddNote}>
            <PlusIcon /> {t("portal.animalDetail.notes.addNew")}
          </button>
        ) : null
      }
    >
      {recentNotes.length === 0 ? (
        <p className="fad-empty">{t("portal.animalDetail.notes.empty")}</p>
      ) : (
        <ul className="fad-recent-notes">
          {recentNotes.map((n) => (
            <li key={n.id ?? `${n.date}-${n.title}`}>
              <span className="fad-recent-date">{n.date ? formatDateShort(n.date, locale) : "—"}</span>
              <span className="fad-recent-main">
                <strong>{n.title || t("portal.animalDetail.log.kind.note")}</strong>
                {n.text ? <span className="fad-recent-text">{n.text}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  ) : null;

  // ── Logboek: everything the foster may see, newest first ─────────────────────────────────────
  const logEntries: LogEntry[] = [
    ...(tileVisible("NOTES")
      ? a.richNotes.map((n): LogEntry => ({
          key: `note-${n.id}`,
          kind: "note",
          date: n.date ?? "",
          title: n.title || t("portal.animalDetail.log.kind.note"),
          detail: n.text,
        }))
      : []),
    ...(tileVisible("GEWICHTEN")
      ? a.recentWeights.map((w): LogEntry => ({
          key: `weight-${w.id}`,
          kind: "weight",
          date: w.measuredAt ?? "",
          title: t("portal.animalDetail.log.kind.weight"),
          detail: `${formatWeight(w.kg, locale)} ${t("portal.animalDetail.weights.unit")}`,
        }))
      : []),
    ...(tileVisible("VOEDING") && a.feeding
      ? a.feeding.log.map((e): LogEntry => ({
          key: `feeding-${e.id}`,
          kind: "feeding",
          date: e.fedAt ?? "",
          title: t("portal.animalDetail.log.kind.feeding"),
          detail:
            [e.amountGrams != null ? `${e.amountGrams}g` : null, e.behavior ? gedragLabel(e.behavior) : null, e.note]
              .filter(Boolean)
              .join(" · ") || null,
        }))
      : []),
    ...(tileVisible("INENTINGEN")
      ? a.vaccinations.map((v): LogEntry => ({
          key: `vacc-${v.id}`,
          kind: "vaccination",
          date: v.date ?? "",
          title: v.name || v.type || t("portal.animalDetail.log.kind.vaccination"),
          detail:
            [v.brand, v.givenBy ? `${t("portal.animalDetail.protection.givenBy")} ${v.givenBy}` : null]
              .filter(Boolean)
              .join(" · ") || null,
        }))
      : []),
  ]
    .filter((e) => e.date !== "")
    .sort((x, y) => y.date.localeCompare(x.date));
  const logIcon = (kind: LogEntry["kind"]) =>
    kind === "note" ? <NoteIcon /> : kind === "weight" ? <ScaleIcon /> : kind === "feeding" ? <BowlIcon /> : <SyringeIcon />;
  const LOG_LIMIT = 15;
  const logVisible = tileVisible("NOTES") || tileVisible("GEWICHTEN") || tileVisible("VOEDING") || tileVisible("INENTINGEN");
  const logTile = logVisible ? (
    <Tile icon={<BookIcon />} title={t("portal.animalDetail.log.title")} footer={
        logEntries.length > LOG_LIMIT ? (
          <button className="fad-seeall" type="button" onClick={() => toggleOpen("LOG")}>
            {isOpen("LOG") ? t("portal.animalDetail.seeLess") : t("portal.animalDetail.seeAll")}
            <ChevronRightIcon />
          </button>
        ) : null
      }>
      {logEntries.length === 0 ? (
        <p className="fad-empty">{t("portal.animalDetail.log.empty")}</p>
      ) : (
        <ul className="fad-log">
          {(isOpen("LOG") ? logEntries : logEntries.slice(0, LOG_LIMIT)).map((e) => (
            <li key={e.key} className={`fad-log-item fad-log-item--${e.kind}`}>
              <span className="fad-log-icon">{logIcon(e.kind)}</span>
              <span className="fad-log-date">{formatDateShort(e.date, locale)}</span>
              <span className="fad-log-main">
                <strong>{e.title}</strong>
                {e.detail ? <span className="fad-recent-text">{e.detail}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  ) : null;

  // ── tabs: a tab exists only when at least one of its tiles is visible under the policy ──────────
  const tabs: { key: DetailTab; label: string; icon: ReactNode }[] = [
    { key: "overview" as const, label: t("portal.animalDetail.tabs.overview"), icon: <GridIcon />, show: true },
    {
      key: "care" as const,
      label: t("portal.animalDetail.tabs.care"),
      icon: <HeartIcon />,
      show: tileVisible("VOEDING") || tileVisible("GEWICHTEN") || tileVisible("WIZARDS"),
    },
    { key: "medical" as const, label: t("portal.animalDetail.tabs.medical"), icon: <PlusIcon width={18} height={18} />, show: tileVisible("INENTINGEN") },
    { key: "notes" as const, label: t("portal.animalDetail.tabs.notes"), icon: <NoteIcon />, show: tileVisible("NOTES") },
    { key: "log" as const, label: t("portal.animalDetail.tabs.log"), icon: <BookIcon />, show: logVisible },
  ].filter((x) => x.show);
  const activeTab: DetailTab = tabs.some((x) => x.key === tab) ? tab : "overview";
  // Overzicht columns — a column whose tiles are all hidden drops out, the rest share the width.
  const overviewColumns = [[weightOverview, feedingOverview], [medicalOverview, notesOverview]].filter(
    (col) => col.some((tile) => tile != null),
  );

  // ── Acties: every entry follows the tile's write level ────────────────────────────────────────
  const actionItems: { key: string; label: string; run: () => void }[] = [];
  if (tileVisible("NOTES") && notesCanEdit) {
    actionItems.push({ key: "note", label: t("portal.animalDetail.actions.addNote"), run: startAddNote });
  }
  if (tileVisible("GEWICHTEN") && weightsCanEdit) {
    actionItems.push({ key: "weight", label: t("portal.animalDetail.actions.addWeight"), run: startAddWeight });
  }
  if (tileVisible("VOEDING") && tileCanEdit("VOEDING")) {
    actionItems.push({
      key: "feeding",
      label: t("portal.animalDetail.actions.addFeeding"),
      run: () => {
        setTab("care");
        startAddLog();
      },
    });
    actionItems.push({
      key: "feeding-plan",
      label: t("portal.animalDetail.actions.editFeeding"),
      run: () => {
        setTab("care");
        startEditFeeding();
      },
    });
  }
  if (tileVisible("INENTINGEN") && tileCanEdit("INENTINGEN")) {
    actionItems.push({ key: "vacc", label: t("portal.animalDetail.actions.addVacc"), run: () => startAddVacc() });
  }
  if (tileVisible("WIZARDS") && tileCanEdit("WIZARDS")) {
    for (const w of a.wizards) {
      actionItems.push({ key: `wizard-${w.wizardType}`, label: w.name || w.wizardType, run: () => startWizard(w) });
    }
  }

  // ── Vorig / Volgend dier ──────────────────────────────────────────────────────────────────────
  const siblings = [...(siblingsQuery.data?.data?.animals ?? [])].sort((x, y) =>
    (y.since ?? "").localeCompare(x.since ?? ""),
  );
  const siblingIndex = siblings.findIndex((s) => s.organizationUuid === organizationUuid && s.dierUuid === dierUuid);
  const openSibling = (offset: number) => {
    const next = siblings[siblingIndex + offset];
    if (next && onOpenAnimal) {
      onOpenAnimal(next.organizationUuid, next.dierUuid);
    }
  };

  return (
    <div className="foster-animal-detail">
      <div className="fad-topbar">
        <button className="fad-back" type="button" onClick={onBack}>
          <ChevronLeftIcon />
          {t("portal.animalDetail.back")}
        </button>
        {onOpenAnimal && siblingIndex >= 0 && siblings.length > 1 ? (
          <div className="fad-pager">
            <button
              className="fad-pager-btn"
              type="button"
              disabled={siblingIndex === 0}
              onClick={() => openSibling(-1)}
            >
              <ChevronLeftIcon /> {t("portal.animalDetail.nav.prev")}
            </button>
            <span className="fad-pager-count">
              {siblingIndex + 1} {t("portal.animalDetail.nav.of")} {siblings.length}
            </span>
            <button
              className="fad-pager-btn"
              type="button"
              disabled={siblingIndex === siblings.length - 1}
              onClick={() => openSibling(1)}
            >
              {t("portal.animalDetail.nav.next")} <ChevronRightIcon />
            </button>
          </div>
        ) : null}
      </div>

      {/* hero — photo, name, species/breed/sex, dates; right column: Acties + the remaining basic data. */}
      <section className="fad-card fad-hero-card">
        <AuthImage
          className="fad-hero-photo"
          path={a.hasPhoto ? `/portal/organizations/${a.organizationUuid}/animals/${a.dierUuid}/photo` : null}
          alt={a.name ? `${t("portal.animals.photoAltNamed")}${a.name}` : t("portal.animals.photoAlt")}
          size="220px"
        />
        <div className="fad-hero-content">
          <div className="fad-hero-title-row">
            <h1>{a.name || "—"}</h1>
            {age ? <span className="fad-age-badge">{age}</span> : null}
          </div>
          <div className="fad-hero-tags">
            {a.species && fieldVisible("BASIS", "diersoort") ? (
              <span>
                <PawIcon width={18} height={18} /> {a.species}
              </span>
            ) : null}
            {a.breed ? (
              <span>
                <ShieldIcon width={18} height={18} /> {a.breed}
              </span>
            ) : null}
            {a.sex && fieldVisible("BASIS", "geslacht") ? (
              <span>
                <SexIcon sex={a.sex} /> {a.sex}
              </span>
            ) : null}
          </div>
          <div className="fad-hero-stats">
            <div className="fad-meta-box">
              <CalendarIcon width={20} height={20} />
              <div>
                <label>{t("portal.animalDetail.field.dob")}</label>
                <strong>{a.dateOfBirth ? formatDateLong(a.dateOfBirth, locale) : "—"}</strong>
              </div>
            </div>
            <div className="fad-meta-box">
              <HomeIcon width={20} height={20} />
              <div>
                <label>{t("portal.animalDetail.field.since")}</label>
                <strong>{a.since ? formatDateLong(a.since, locale) : "—"}</strong>
              </div>
            </div>
            {stayDays != null ? (
              <div className="fad-meta-box">
                <CalendarIcon width={20} height={20} />
                <div>
                  <label>{t("portal.animalDetail.stat.stayDuration")}</label>
                  <strong>
                    {stayDays} {stayDays === 1 ? t("portal.animalDetail.stat.day") : t("portal.animalDetail.stat.days")}
                  </strong>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {actionItems.length > 0 || heroFacts.length > 0 || heroCanEdit ? (
          <div className="fad-hero-side">
            {actionItems.length > 0 ? (
              <div className="fad-actions" ref={actionsRef}>
                <button
                  className="fad-actions-btn"
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={actionsOpen}
                  onClick={() => setActionsOpen((o) => !o)}
                >
                  {t("portal.animalDetail.actions.title")}
                  <ChevronDownIcon />
                </button>
                {actionsOpen ? (
                  <ul className="fad-actions-menu" role="menu">
                    {actionItems.map((item) => (
                      <li key={item.key} role="none">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setActionsOpen(false);
                            item.run();
                          }}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {heroFacts.length > 0 || heroCanEdit ? (
              <div className="fad-hero-facts">
                <div className="fad-hero-facts-head">
                  <span>
                    <ClipboardIcon width={16} height={16} /> {t("portal.animalDetail.tile.basis")}
                  </span>
                  {heroCanEdit && !editing ? (
                    <button className="fad-edit-btn" type="button" onClick={startEdit}>
                      <PencilIcon /> {t("portal.animalDetail.edit.edit")}
                    </button>
                  ) : null}
                </div>
                <dl>
                  {heroFacts.map((fact) => (
                    <div key={fact.key} className="fad-hero-fact">
                      <dt>{fact.label}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
                {editing ? (
                  <div className="fad-edit-actions">
                    {saveError ? <span className="fad-error">{saveError}</span> : null}
                    <button className="fad-btn fad-btn--ghost" type="button" onClick={cancelEdit} disabled={saving}>
                      {t("portal.animalDetail.edit.cancel")}
                    </button>
                    <button className="fad-btn fad-btn--primary" type="button" onClick={saveEdit} disabled={saving}>
                      {saving ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.edit.save")}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {tabs.length > 1 ? (
        <div className="fad-tabs" role="tablist" aria-label={t("portal.animalDetail.tabs.label")}>
          {tabs.map((x) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              aria-selected={activeTab === x.key}
              className={`fad-tab${activeTab === x.key ? " is-active" : ""}`}
              onClick={() => setTab(x.key)}
            >
              {x.icon}
              {x.label}
            </button>
          ))}
        </div>
      ) : null}

      {activeTab === "overview" ? (
        <div className="fad-overview" data-cols={overviewColumns.length}>
          {overviewColumns.map((col, i) => (
            <div className="fad-overview-col" key={i}>
              {col.map((tile, j) => (
                <Fragment key={j}>{tile}</Fragment>
              ))}
            </div>
          ))}
        </div>
      ) : null}
      {activeTab === "care" ? (
        <div className="fad-grid fad-grid--care">
          {feedingTile}
          {weightsTile}
          {wizardsTile}
        </div>
      ) : null}
      {activeTab === "medical" ? <div className="fad-grid fad-grid--single">{protectionTile}</div> : null}
      {activeTab === "notes" ? <div className="fad-grid fad-grid--single">{notesTile}</div> : null}
      {activeTab === "log" ? <div className="fad-grid fad-grid--single">{logTile}</div> : null}

      {/* Media & Documenten stays out of the portal (AVG/GDPR) — the backend media endpoints remain, but the
          portal renders no foster-facing media. */}


      {renderVaccModal()}
      {renderNoteModal()}
      {weightForm ? (
        <Modal
          open
          title={t("portal.animalDetail.weights.add")}
          onClose={() => setWeightForm(null)}
          footer={
            <>
              {writeError ? <span className="fad-error">{writeError}</span> : null}
              <Button variant="secondary" onClick={() => setWeightForm(null)} disabled={writeSaving}>
                {t("portal.animalDetail.edit.cancel")}
              </Button>
              <Button onClick={saveWeight} disabled={writeSaving}>
                {writeSaving ? t("portal.animalDetail.edit.saving") : t("portal.animalDetail.edit.save")}
              </Button>
            </>
          }
        >
          <div className="fad-form-row">
            <label className="fad-form-field">
              <span>{t("portal.animalDetail.feeding.column.date")}</span>
              <input
                className="fad-input"
                type="date"
                max={todayIso()}
                value={weightForm.date}
                onChange={(e) => setWeightForm((f) => (f ? { ...f, date: e.target.value } : f))}
              />
            </label>
            <label className="fad-form-field">
              <span>{t("portal.animalDetail.weights.kg")}</span>
              <input
                className="fad-input"
                type="text"
                inputMode="decimal"
                placeholder={t("portal.animalDetail.weights.kgPlaceholder")}
                value={weightForm.kg}
                onChange={(e) => setWeightForm((f) => (f ? { ...f, kg: e.target.value } : f))}
              />
            </label>
          </div>
        </Modal>
      ) : null}
      {activeWizard ? (
        <PortalWizardRunner
          base={base}
          run={activeWizard.run}
          wizardName={activeWizard.name}
          animal={a}
          age={age}
          onClose={(completed) => {
            if (completed) {
              setFinishedWizard(activeWizard.run.wizardType);
            }
            setActiveWizard(null);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
